import * as React from "react"
import {
  Box,
  Stat,
  StatLabel,
  StatNumber,
  Center,
  Spinner,
  Button,
  Flex,
  Input,
  IconButton,
  Text,
  Select,
  Stack,
} from "@chakra-ui/react"
import { RiArrowDownLine, RiArrowUpLine, RiInformationLine } from "react-icons/ri"
import { BiCheck, BiX } from "react-icons/bi"
import { HiOutlinePlus } from "react-icons/hi"

import { ZoneType, ZoneEntity } from "../lib/types/zone"
import { ZoneItem } from "./ZoneItem"
import { Bbox, MultiPolygonData } from "../lib/helpers/map"
import { Order } from "../lib/types/shared"
import { ZONE_LABELS, ZONE_DESCRIPTIONS } from "../lib/static/zone"
import { NoData } from "./NoData"
import { useMutation } from "react-query"
import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { useOrgs } from "./providers/OrgProvider"
import { Sidebar } from "./Sidebar"

interface Props {
  type: ZoneType
  zones: ZoneEntity[]
  isLoading: boolean
  order: Order
  count: number
  polygonsData?: MultiPolygonData | undefined
  isCreating: boolean
  editableZone: ZoneEntity | null
  onSetBbox: (bbox: Bbox) => void
  onSetOrder: (order: Order) => void
  refetch: () => void
  setEditableZone: (zone: ZoneEntity | null) => void
  setIsCreating: (boolean: boolean) => void
  selectedZone?: ZoneEntity
  handleSetSelectedZone: (zone: ZoneEntity | undefined) => void
}

interface CreateRequest {
  type: ZoneType
  name: string | null
  organization: string
  polygon?: {
    coordinates?: MultiPolygonData
    type: string
  }
}

export function ZonesMapSidebar({
  onSetBbox,
  refetch,
  setEditableZone,
  polygonsData,
  isCreating,
  editableZone,
  selectedZone,
  ...props
}: Props) {
  const [name, setName] = React.useState<string>("")
  const toast = useToast()
  const { currentOrgs } = useOrgs()
  const [organization, setOrganization] = React.useState<string>()
  const refs = props.zones.reduce((acc, value) => {
    acc[value.uuid] = React.createRef()
    return acc
  }, {} as { [key: string]: React.RefObject<HTMLDivElement> })

  const organizationOptions = currentOrgs.map((org) => ({
    label: org.name,
    value: org.url,
    id: org.uuid,
  }))

  const { mutateAsync: create, isLoading } = useMutation<ZoneEntity, unknown, CreateRequest>((data) =>
    fetcher(`zones/`, { body: JSON.stringify(data), method: "post" }),
  )

  const handleCreateZone = async (e: any) => {
    e.preventDefault()
    try {
      if (!polygonsData || polygonsData[0].length === 0)
        return toast({ status: "error", description: "Please create a valid zone shape" })
      if (currentOrgs.length > 1 && !!!organization)
        return toast({ status: "error", description: "Please select an organization" })
      const res = await create({
        type: props.type,
        name,
        polygon: { coordinates: polygonsData, type: "MultiPolygon" },
        organization: currentOrgs.length > 1 ? organization! : currentOrgs[0].url,
      })
      if (res?.uuid) {
        props.setIsCreating(false)
        setName("")
        refetch()
        toast({ title: "Successfully created zone", status: "success" })
      } else {
        toast({ title: "Error creating zone", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  React.useEffect(() => {
    if (refs && selectedZone && refs[selectedZone.uuid]) {
      refs[selectedZone.uuid].current?.scrollIntoView({
        behavior: "smooth",
        inline: "nearest",
        block: "nearest",
      })
    }
  }, [selectedZone, refs])

  return (
    <Sidebar sidebarKey="noa:zones:map" styleControl>
      {props.isLoading ? (
        <Center h={200} p={6}>
          <Spinner />
        </Center>
      ) : (
        <Flex h="100%" p={6} pb={0} direction="column" overflowY="scroll">
          <Box pt={2}>
            <Stat mb={2}>
              <StatLabel>Total {ZONE_LABELS[props.type]}</StatLabel>
              <StatNumber>{props.zones.length}</StatNumber>
            </Stat>
            <Flex align="center" border="1px solid" borderColor="gray.100" p={2} mb={4} borderRadius="lg">
              <Box as={RiInformationLine} boxSize="22px" mx={2} />
              <Text ml={1} fontSize="0.8rem">
                {ZONE_DESCRIPTIONS[props.type]}
              </Text>
            </Flex>
            <Flex justify="space-between">
              <Button
                size="sm"
                leftIcon={<HiOutlinePlus />}
                onClick={() => props.setIsCreating(true)}
                colorScheme="green"
                isDisabled={!!editableZone || isCreating}
              >
                New {ZONE_LABELS[props.type].slice(0, -1)}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  props.onSetOrder({ order: props.order.order === "-" ? "" : "-", orderBy: "modified" })
                }
                leftIcon={
                  <Box as={props.order.order === "-" ? RiArrowDownLine : RiArrowUpLine} boxSize="18px" />
                }
              >
                {props.order.order === "-" ? "Newest" : "Oldest"}
              </Button>
            </Flex>
          </Box>
          <Box>
            {isCreating && (
              <Box as="form" w="100%" onSubmit={handleCreateZone} mb={2} p={3} bg="gray.50" borderRadius="md">
                <Text w="100%" fontWeight={600}>
                  New zone
                </Text>
                <Flex align="center" justify="space-between">
                  <Stack>
                    <Input
                      autoFocus
                      isRequired
                      bg="white"
                      value={name}
                      placeholder="Zone name"
                      onChange={(e) => setName(e.target.value)}
                    />
                    {currentOrgs.length > 1 && (
                      <Select bg="white" onChange={(e) => setOrganization(e.target.value)}>
                        <option value="">Select an organization</option>
                        {organizationOptions.map((option) => (
                          <option key={option.id} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Stack>
                  <Center mr={2}>
                    <IconButton
                      size="sm"
                      ml={2}
                      variant="ghost"
                      colorScheme="green"
                      aria-label="Create zone"
                      icon={<Box as={BiCheck} boxSize="22px" />}
                      isLoading={isLoading}
                      type="submit"
                    />
                    <IconButton
                      size="sm"
                      ml={2}
                      variant="ghost"
                      colorScheme="red"
                      aria-label="Cancel create zone"
                      icon={<Box as={BiX} boxSize="22px" />}
                      onClick={() => props.setIsCreating(false)}
                    />
                  </Center>
                </Flex>
              </Box>
            )}
            {props.zones.length > 0 ? (
              <Box my={4}>
                {props.zones.map((zone, index) => {
                  const zoneOrg = currentOrgs.find(({ url }) => url === zone.organization)!
                  return (
                    <ZoneItem
                      key={zone.uuid}
                      {...{
                        ref: refs[zone.uuid],
                        onSetBbox,
                        refetch,
                        organization: zoneOrg,
                        index,
                        zone,
                        handleSelectZone: () => props.handleSetSelectedZone(zone),
                        isSelected: zone.uuid === selectedZone?.uuid,
                        editableZone,
                        setEditableZone,
                        polygonCoords: polygonsData,
                        isCreating,
                      }}
                    />
                  )
                })}
              </Box>
            ) : (
              <Center h={200}>
                <NoData>No Zones</NoData>
              </Center>
            )}
          </Box>
        </Flex>
      )}
    </Sidebar>
  )
}
